
import React from "react";
import "./../custom-fonts.css";

export default function MathematicalMedicine() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;

    const y =
      el.getBoundingClientRect().top +
      window.pageYOffset -
      72; // mobile-safe offset

    window.scrollTo({
      top: y,
      behavior: "smooth",
    });
  };

  const user = "ben.melchers";
  const domain = "stanford.edu";
  const email = `${user}@${domain}`;

  return (
    <div className="root">
      {/* Fixed background layer (mobile-safe) */}
      <div className="background-layer" />

      {/* Content layer */}
      <div className="content-layer">
        <style>{`
/* =========================
   PERFORMANCE HINTS
   ========================= */

.section,
.hero,
.card {
  transform: none;
  will-change: auto;
}

/* =========================
   VARIABLES
   ========================= */

:root {
  --bg-main: #03152f;
  --bg-soft: #082347;

  --cardinal: #ffb45c;
--cardinal-soft: rgba(255,180,92,0.35);
--cardinal-faint: rgba(255,180,92,0.12);

  --text-main: #e5e7eb;
  --text-sub: #9ca3af;
}

/* =========================
   ROOT
   ========================= */

.root {
  font-family: Inter, system-ui, -apple-system;
  min-height: 100vh;
  color: var(--text-main);
  overflow-x: hidden;
  position: relative;

background:
  radial-gradient(
    900px 700px at 18% 22%,
    rgba(77,120,255,0.08),
    transparent 60%
  ),
  radial-gradient(
    800px 600px at 82% 38%,
    rgba(30,80,180,0.06),
    transparent 65%
  ),
  linear-gradient(
    180deg,
    #03152f 0%,
    #082347 45%,
    #03152f 100%
  );
}

/* =========================
   FIXED BACKGROUND LAYER
   ========================= */

.background-layer {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;

  background:
    repeating-linear-gradient(
      0deg,
      rgba(255,255,255,0.015) 0px,
      rgba(255,255,255,0.015) 1px,
      transparent 1px,
      transparent 34px
    ),
    repeating-linear-gradient(
      90deg,
      rgba(255,255,255,0.015) 0px,
      rgba(255,255,255,0.015) 1px,
      transparent 1px,
      transparent 34px
    ),
    repeating-conic-gradient(
      from 12deg at 50% 50%,
      rgba(140,21,21,0.018) 0deg 0.8deg,
      transparent 0.8deg 18deg
    );

  filter: blur(1px);
  opacity: 0.28;
}

.content-layer {
  position: relative;
  z-index: 1;
}

/* =========================
   HERO
   ========================= */

.hero {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-core {
  max-width: 960px;
  padding: 4rem 2rem;
}

.hero-title {
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 0.95;
}

.hero-title-main {
  font-family: FFDaxProBold;
  font-size: clamp(2.7rem, 6vw, 4rem);
  letter-spacing: -0.055em;
}

.hero-title-sub {
  font-family: FFDaxProBold;
  font-size: clamp(3rem, 7vw, 4.6rem);
  letter-spacing: -0.075em;
  margin-top: -0.15em;
  color: var(--cardinal);
  position: relative;
}

.hero-title-sub::after {
  content: "";
  position: absolute;
  left: 7%;
  bottom: -0.22em;
  width: 86%;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
rgba(255,180,92,0.75),
    transparent
  );
  opacity: 0.5;
}

/* =========================
   ACTIONS
   ========================= */

.actions {
  margin-top: 5.4rem;
  display: flex;
  justify-content: center;
  gap: 3.8rem;
  flex-wrap: wrap;
}

.action {
  background: none;
  border: none;
  padding: 1rem 0.6rem 1.2rem;
  cursor: pointer;
  font-family: FFDaxProRegular;
  font-size: 0.7rem;
  letter-spacing: 0.42em;
  text-transform: uppercase;
  color: rgba(229,231,235,0.75);
  position: relative;
}

.action::before {
  content: "";
  position: absolute;
  left: 0;
  bottom: 0.2rem;
  width: 100%;
  height: 1px;
  background: rgba(255,180,92,0.7);
  transform: scaleX(0.26);
  transition: transform 420ms cubic-bezier(.19,1,.22,1);
}

.action:hover::before {
  transform: scaleX(1);
}

/* =========================
   SECTIONS / CARDS
   ========================= */

.section {
  padding: 8rem 1.5rem;
  display: grid;
  place-items: center;
}

.card {
  max-width: 880px;
  padding: 3.8rem 3.2rem;
  border-radius: 16px;
  border: 1px solid rgba(255,255,255,0.08);
background: linear-gradient(
  180deg,
  rgba(9,32,63,0.94),
  rgba(4,18,40,0.96)
);
  box-shadow: 0 28px 70px rgba(0,0,0,0.55);
}

.tag {
  font-size: 0.85rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  font-family: FFDaxProRegular;
}

.title {
  font-size: clamp(2.1rem, 5vw, 3.3rem);
  margin: 1rem 0 1.5rem;
  font-family: FFDaxProRegular;
}

.rule {
  height: 1px;
  background: rgba(255,255,255,0.1);
  margin: 2.2rem 0;
}

.copy {
  font-size: 1.05rem;
  font-family: FFDaxProRegular;
  line-height: 1.75;
  color: var(--text-sub);
  margin-bottom: 1.4rem;
}

.axiom {
  display: inline-block;
  padding: 0.3rem 0.45rem;
  margin: 0.2rem 0 0.8rem; /* 👈 adds bottom spacing */
background: rgba(255,180,92,0.12);
border-left: 3px solid var(--cardinal);
color: var(--text-main);
  font-weight: 500;
}


.case-label {
  font-family: FFDaxProRegular;
  font-size: 0.6rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #9ca3af;

  margin: 0.6rem 0;        /* vertical spacing */
  padding: 0.4rem 0.6rem;  /* adds “breathing room” inside */
  border-radius: 6px;      /* subtle rounded corners */
  background: rgba(156,163,175,0.05); /* optional faint highlight */
}


/* =========================
   SUPPORT WELL
   ========================= */

.support-well {
  margin-top: 2.6rem;
  padding: 1.8rem 1.9rem;
  border-radius: 12px;
background: linear-gradient(
  180deg,
  rgba(255,180,92,0.04),
  rgba(5,20,42,0.45)
);
  box-shadow:
    inset 0 1px 2px rgba(255,255,255,0.05),
    inset 0 -2px 4px rgba(0,0,0,0.35);
  border: 1px solid rgba(255,255,255,0.05);
}

/* =========================
   FOOTER
   ========================= */

.footer {
  padding: 4.5rem 1rem;
  text-align: center;
  font-size: 0.65rem;
  font-family: FFDaxProRegular;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #6b7280;
  border-top: 1px solid rgba(255,255,255,0.08);
}

/* =========================
   MOBILE
   ========================= */

@media (max-width: 760px) {
  .hero { min-height: 92vh; }
  .section { padding: 5rem 1rem; }
  .card { padding: 2.4rem 1.4rem; }
  .copy { font-size: 0.95rem; }
}
        `}</style>

<section className="hero hero--decision">
        <div className="hero-core">
          <h1 className="hero-title">
            <span className="hero-title-main">Mathematical</span>
            <span className="hero-title-sub">Medicine</span>
          </h1>

          <div className="actions actions--decision">
            <button className="action" onClick={() => scrollTo("whatisit")}>Define the Field</button>
            <button className="action" onClick={() => scrollTo("whyitworks")}>Understand the Advantage</button>
            <button className="action" onClick={() => scrollTo("earlyvictories")}>See the Evidence</button>
            <button className="action" onClick={() => scrollTo("economicimpact")}>Assess the Impact</button>
            <button className="action" onClick={() => scrollTo("translation")}>Translate Worldwide</button>
            <button className="action action--primary" onClick={() => scrollTo("fordonors")}>
              Establish the Future
            </button>
          </div>
        </div>
      </section>

      <section className="section" id="whatisit">
  <div className="card">
    <div className="tag">Define the Field</div>
    <h2 className="title">Structure Over Scale</h2>
    <div className="rule" />
    <div className="copy">
      <span className="axiom">
        Mathematical Medicine replaces brute-force data and GPUs with powerful mathematical structure, so medical insight becomes inevitable.
      </span>
      <div className="copy">
        Traditional medicine tries to solve complexity by scaling outward with massive data sets, GPU clusters, and black-box correlations. Mathematical Medicine scales inward, using small but information-dense and structure-first mathematics to produce clear, explainable insight.
      </div>
      <div className="copy">
        The mathematician Mikhail Gromov has said, “This area does not yet exist. It will have to be invented.” Mathematical Medicine represents one possible construction of such an area.
      </div>
    </div>
  </div>
</section>

<section className="section" id="whyitworks">
  <div className="card">
    <div className="tag">Understand the Advantage</div>
    <h2 className="title">Structure Beats Scale Every Time</h2>
    <div className="rule" />
    <div className="copy">
      <div className="copy">
        Mathematical Medicine reveals meaning that brute-force statistics miss.
      </div>
      <span className="axiom">
        Instead of averaging patients into cohorts or learning correlations at scale,
        it looks for internally consistent structure that explains all features of an
        individual at once.
      </span>
      <div className="copy">
        What traditional methods dismiss as noise or outliers often turns out to be the
        decisive structure that leads to the correct diagnosis.
      </div>
    </div>
  </div>
</section>

<section className="section" id="earlyvictories">
  <div className="card">
    <div className="tag">See the Evidence</div>
    <h2 className="title">Proof in Practice</h2>
    <div className="rule" />
    <div className="case-label">Case Resolution</div>

    <div className="copy">
    A patient endured years of misdiagnosis because standard statistical medicine produced ambiguity rather than clarity. By examining overall consistency across the patient’s clinical information instead of isolated tests, Mathematical Medicine revealed a rare condition—showing that the solution came from structure, not pattern-matching.
    </div>

    <div className="copy">
    Another patient with persistent weight gain was found to have an underlying genetic dysregulation that conventional population-level approaches had failed to identify. Using Mathematical Medicine, a patient-specific source of imbalance was isolated, enabling a targeted protocol that led to dramatic improvement in weight.
    </div>

    <div className="case-label">Demonstrated Capability</div>

    <div className="copy">
      <span className="axiom">
      Mathematical Medicine demonstrated that cancer can be distinguished from non-cancer using quantitative structure alone, without dyes or labels, revealing signal that conventional pathology overlooks.
      </span>
      Based on these results, oncologists now want to apply Mathematical Medicine as an immediate next step in stage IV cancer, extracting clinically relevant insight from existing data that is currently unused or discarded.
    </div>
  </div>
</section>

<section className="section" id="economicimpact">
  <div className="card">
    <div className="tag">Assess the Impact</div>
    <h2 className="title">Hedge Against A.I. Energy Limits</h2>
    <div className="rule" />
    <div className="copy">
      <span className="axiom">
        Mathematical Medicine replaces brute-force computation with mathematical structure, allowing problems that require clusters and megawatts today to run on workstation or laptop-scale hardware.
      </span>
      Funding Mathematical Medicine provides an energy-efficient hedge to existing Big Data investments—a framework shift in which insight can scale faster than compute, cost, or carbon.
    </div>
  </div>
</section>

<section className="section" id="translation">
  <div className="card">
    <div className="tag">Translate Worldwide</div>
    <h2 className="title">Scalable Clinical Insight</h2>
    <div className="rule" />
    <div className="copy">

      <span className="axiom">
        Mathematical Medicine is designed to translate directly into clinical practice.
      </span>
      <div className="copy">
      It is focused on the development of an intermediate translation layer between cohort-based statistical models and individualized multi-omics diagnosis and clinical decision-making. Without this mathematical layer, the clinical adoption of multi-omics data- particularly for complex cases- has been limited.  As a result, many complex, multi-system conditions remain undiagnosed or misdiagnosed for long periods, delaying effective treatment and, in some cases, allowing disease processes to worsen.
      </div>
      <div className="copy">
      Its structure-based framework is already applied to rare conditions, complex cancers, and chronic disorders like ME/CFS and MCAS, where conventional methods often fail. By understanding a condition’s underlying structure, insights become reusable and shareable, expanding high-quality diagnostic access worldwide.
      </div>
    </div>
  </div>
</section>

<section className="section" id="fordonors">
  <div className="card">
    <div className="tag">Establish the Future</div>
    <h2 className="title">Creating Medicine's Future</h2>
    <div className="rule" />
    <div className="copy">

      <div className="copy">
        <span className="axiom">
          Your support establishes a new scientific discipline. It does not fund incremental A.I. scale, but advances a mathematically grounded framework for medicine that replaces brute-force computation with durable structure.
        </span>
      </div>

      <div className="copy">
      Biomedical research is hitting a structural limit: data continues to grow, but insight lags, constrained by energy, computation, and diminishing returns. Mathematical Medicine overcomes this limit by emphasizing internal consistency, mathematical structure, and explainability, unlocking actionable insight from existing data.
      </div>

      <div className="support-well">

        <div className="copy">
          For philanthropic partnerships and inquiries, please contact Ben Melchers at{" "}
            <a
              href={`mailto:${email}`}
              style={{ color: "#ffca84", textDecoration: "underline" }}
              >
              {email}
            </a>
        </div>

        <div className="copy" style={{ fontSize: "0.95rem" }}>
          For more than three decades, the Stanford Genome Technology Center has been at the forefront of medical innovation. Mathematical Medicine is the next step in that trajectory—a new framework for making biological data intelligible. This is the frontier we are now pioneering.
        </div>
      </div>


    </div>
  </div>
</section>

        <footer className="footer">
          © {new Date().getFullYear()} Stanford Genome Technology Center
        </footer>
      </div>
    </div>
  );
}
